/**
 * Offerings — three numbered editorial chapters (shipyard, industrial,
 * construction & infrastructure). Alternating photo/text compositions.
 * Photographs illustrate the sector only; they are not images of named projects.
 */

import { memo } from "react";
import { useTranslation } from "react-i18next";
import Reveal from "@/components/common/Reveal";
import shipyardImage from "@/assets/shipyard-vessel-v2.jpg";
import industrialImage from "@/assets/people-leadership.jpg";
import constructionImage from "@/assets/terminal-port-development-2.jpg";

const IMAGES = [shipyardImage, industrialImage, constructionImage];

interface Card {
  title: string;
  summary: string;
  items: string[];
  imageAlt: string;
}

const Offerings = () => {
  const { t } = useTranslation();
  const cards = t("home.offer.cards", { returnObjects: true }) as Card[];

  return (
    <section id="scope" className="bg-background scroll-mt-20" aria-labelledby="scope-heading">
      <div className="container mx-auto px-6 pt-20 md:pt-28 pb-6 md:pb-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-10 items-end">
          <Reveal className="lg:col-span-7">
            <div className="flex items-center gap-4 mb-5">
              <span className="rule w-10 shrink-0" aria-hidden="true" />
              <p className="eyebrow text-cobalt">{t("home.offer.label")}</p>
            </div>
            <h2 id="scope-heading" className="display-lg text-foreground">
              {t("home.offer.heading")}
            </h2>
          </Reveal>
          <Reveal delay={90} className="lg:col-span-5">
            <p className="text-base md:text-lg leading-relaxed text-muted-foreground lg:border-l lg:border-border lg:pl-6">
              {t("home.offer.intro")}
            </p>
          </Reveal>
        </div>
      </div>

      <div className="container mx-auto px-6 pb-20 md:pb-28">
        {cards.map((card, index) => {
          const flipped = index % 2 === 1;
          return (
            <Reveal
              as="article"
              key={card.title}
              className="border-t border-border py-10 md:py-14"
            >
              <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-center">
                <div
                  className={`lg:col-span-7 ${flipped ? "lg:order-2" : ""} group overflow-hidden bg-muted`}
                >
                  <img
                    src={IMAGES[index]}
                    alt={card.imageAlt}
                    className="aspect-[16/9] w-full object-cover object-center transition-transform duration-[900ms] ease-out group-hover:scale-[1.04]"
                    width="1600"
                    height="900"
                    loading="lazy"
                    decoding="async"
                  />
                </div>

                <div className={`lg:col-span-5 ${flipped ? "lg:order-1" : ""}`}>
                  <div className="flex items-baseline gap-4 mb-4">
                    <span className="index-num text-4xl md:text-5xl text-cobalt/25">
                      {String(index + 1).padStart(2, "0")}
                    </span>
                    <span className="rule flex-1" aria-hidden="true" />
                  </div>
                  <h3 className="display-md text-foreground mb-3">{card.title}</h3>
                  <p className="text-muted-foreground leading-relaxed mb-6">{card.summary}</p>
                  <ul className="divide-y divide-border border-y border-border">
                    {card.items.map((item) => (
                      <li
                        key={item}
                        className="flex items-start gap-3 py-3 text-sm md:text-base text-foreground/90"
                      >
                        <span
                          className="mt-2.5 h-[3px] w-4 shrink-0 bg-cobalt"
                          aria-hidden="true"
                        />
                        <span className="leading-snug">{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </Reveal>
          );
        })}
      </div>
    </section>
  );
};

export default memo(Offerings);
